import { usePage } from "@inertiajs/react";
import { Shield } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  GROUP_LABELS,
  type GroupKey,
  getAvatarColorByGroups,
} from "@/constants/groups";
import { useInitials } from "@/hooks/use-initials";
import type { SharedData, User } from "@/types";

export function UserInfo({
  user,
  showEmail = false,
}: {
  user: User;
  showEmail?: boolean;
}) {
  const getInitials = useInitials();
  const { auth } = usePage<SharedData>().props;
  const groups: GroupKey[] = auth?.groups ?? [];
  const avatarColorClass = getAvatarColorByGroups(groups);

  return (
    <>
      <Avatar className="h-8 w-8 overflow-hidden rounded-full">
        <AvatarImage src={user.avatar} alt={user.name} />
        <AvatarFallback className={`rounded-lg ${avatarColorClass}`}>
          {getInitials(user.name, user.email)}
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-medium">{user.name}</span>
        {showEmail && (
          <span className="text-muted-foreground truncate text-xs">
            {user.email}
          </span>
        )}
      </div>
      {groups.length > 0 && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground flex items-center transition-colors"
                aria-label="Ver grupos"
              >
                <Shield className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" align="end">
              <p className="font-medium">Tus grupos:</p>
              <ul className="mt-1 list-inside list-disc text-xs">
                {groups.map((g) => (
                  <li key={g}>{GROUP_LABELS[g] ?? g}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </>
  );
}
